import { LitElement, html, nothing, type TemplateResult, type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import styles from './styles.js';
import type { MenuItemCommandEvent, MenuItemSelectEventDetail } from '../types.js';
import { emit } from '../utils/dom.utils.js';

/* ─────────────────────────────────────────────────────────────────────────────
   Data types
───────────────────────────────────────────────────────────────────────────── */

/** A panel-menu item (used for both root panels and nested items). */
export interface PanelMenuItem {
  /** Display text. */
  label?: string;
  /** Icon name rendered before the label (resolved via the configured library). */
  icon?: string;
  /** Name of the icon library registered via `registerIconLibrary`. Defaults to `'default'`. */
  iconLibrary?: string;
  /** Additional CSS class(es) for the icon wrapper. */
  iconClass?: string;
  /** Callback when item is activated. Leaf items only. */
  command?: (e: MenuItemCommandEvent) => void;
  /** Renders item as an `<a>` link. Leaf items only. */
  url?: string;
  /** Anchor target attribute. Used with url. */
  target?: string;
  /** Child items. Root level = sub-menu items; nested = deeper sub-items. */
  items?: PanelMenuItem[];
  /** Disabled — visible but not interactive. */
  disabled?: boolean;
  /** When false the item is excluded from rendering entirely. */
  visible?: boolean;
  /**
   * Horizontal separator rule.
   * Valid only inside a panel's item list (not at root level).
   */
  separator?: boolean;
  /** Badge text at the trailing edge. */
  badge?: string;
  /** Additional CSS class(es) for the badge span. */
  badgeClass?: string;
  /** Native browser tooltip. */
  title?: string;
  /** Unique identifier — used for keying and ARIA. */
  id?: string;
  /** Arbitrary data payload; passed through events. */
  data?: unknown;
  /** Danger / destructive visual style. */
  danger?: boolean;
  /**
   * Initial expanded state.
   * At root level: controls whether the accordion panel starts open.
   * At nested level: controls whether sub-items start expanded.
   */
  expanded?: boolean;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Event detail types
───────────────────────────────────────────────────────────────────────────── */

export interface PanelMenuOpenEventDetail   { item: PanelMenuItem; }
export interface PanelMenuCloseEventDetail  { item: PanelMenuItem; }
export interface PanelMenuExpandEventDetail { item: PanelMenuItem; key: string; }
export interface PanelMenuCollapseEventDetail { item: PanelMenuItem; key: string; }

/* ─────────────────────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────────────────────── */

/**
 * `uwc-panelmenu`
 *
 * A vertical accordion navigation component. Top-level items render as
 * collapsible panel headers. Each panel's `items` render as a nested,
 * indented menu tree supporting unlimited depth.
 *
 * All colours — including item hover states, separators, and badges —
 * cascade automatically from `--uwc-panelmenu-bg` and `--uwc-panelmenu-color`.
 *
 * @attr items         — `PanelMenuItem[]` navigation model.
 * @attr multiple      — Allow multiple panels open simultaneously. Default: `true`.
 * @attr scroll-height — CSS max-height for the container (e.g. `"400px"`).
 * @attr aria-label    — Accessible label for the landmark. Default: `'Navigation'`.
 *
 * @prop activeItem    — Pre-select an item on load. Matched by `id` first,
 *                       then by object reference. Setting this automatically
 *                       opens the parent panel and expands ancestor items.
 *                       Set to `null` to clear selection.
 *
 * @fires uwc-item-select    — `{ item, originalEvent }` — leaf item activated.
 * @fires uwc-panel-open     — `{ item }` — root panel expanded.
 * @fires uwc-panel-close    — `{ item }` — root panel collapsed.
 * @fires uwc-item-expand    — `{ item, key }` — nested item expanded.
 * @fires uwc-item-collapse  — `{ item, key }` — nested item collapsed.
 *
 * @cssprop --uwc-panelmenu-bg
 * @cssprop --uwc-panelmenu-color
 * @cssprop --uwc-panelmenu-border
 * @cssprop --uwc-panelmenu-radius
 * @cssprop --uwc-panelmenu-shadow
 * @cssprop --uwc-panelmenu-panel-bg
 * @cssprop --uwc-panelmenu-panel-border
 * @cssprop --uwc-panelmenu-panel-radius
 * @cssprop --uwc-panelmenu-header-padding
 * @cssprop --uwc-panelmenu-header-font-size
 * @cssprop --uwc-panelmenu-header-font-weight
 * @cssprop --uwc-panelmenu-header-hover-bg
 * @cssprop --uwc-panelmenu-header-active-bg
 * @cssprop --uwc-panelmenu-item-padding
 * @cssprop --uwc-panelmenu-item-font-size
 * @cssprop --uwc-panelmenu-item-font-weight
 * @cssprop --uwc-panelmenu-item-hover-bg
 * @cssprop --uwc-panelmenu-item-active-bg
 * @cssprop --uwc-panelmenu-item-danger-color
 * @cssprop --uwc-panelmenu-item-selected-bg
 * @cssprop --uwc-panelmenu-item-selected-color
 * @cssprop --uwc-panelmenu-indent
 * @cssprop --uwc-panelmenu-separator-color
 * @cssprop --uwc-panelmenu-badge-bg
 * @cssprop --uwc-panelmenu-badge-color
 */

export class UwcPanelMenu extends LitElement {
  static override styles = styles;

  /* ── Public properties ───────────────────────────────────────────── */

  @property({ type: Array })
  items: PanelMenuItem[] = [];

  @property({ type: Boolean, reflect: true })
  multiple = true;

  @property({ attribute: 'scroll-height' })
  scrollableHeight = '';

  @property({ attribute: 'aria-label' })
  ariaLabel = 'Navigation';

  /**
   * The currently selected item. Matched by `id` first, then by object
   * reference. When set, the parent panel is automatically opened and all
   * ancestor items are expanded. Set to `null` to clear selection.
   */
  @property({ attribute: false })
  activeItem: PanelMenuItem | null = null;

  /* ── Internal state ──────────────────────────────────────────────── */

  /**
   * Set of root-level panel keys that are currently open.
   * Keys are auto-generated as "p-{index}" if item.id is absent.
   */
  @state() private _openPanels: Set<string> = new Set();

  /**
   * Set of nested item path-keys that are currently expanded.
   * Keys are dot-separated paths, e.g. "p-0.1.2".
   */
  @state() private _expandedItems: Set<string> = new Set();

  /**
   * The currently keyboard-focused item path, used for roving focus
   * across the entire panel-menu.
   */
  @state() private _focusedKey: string | null = null;

  /** The path-key of the currently selected leaf item. */
  @state() private _selectedKey: string | null = null;

  /* ── Lifecycle ───────────────────────────────────────────────────── */

  override willUpdate(changed: PropertyValues): void {
    super.willUpdate(changed);
    if (changed.has('items')) {
      this._initExpandedState();
    }
    if ((changed.has('items') || changed.has('activeItem')) && this.activeItem) {
      this._resolveActiveItem(this.activeItem);
    } else if (changed.has('activeItem') && !this.activeItem) {
      this._selectedKey = null;
    }
  }

  /** Seed open-panel and expanded-item state from item.expanded flags. */
  private _initExpandedState(): void {
    const panels = new Set<string>();
    const items  = new Set<string>();

    this.items.forEach((item, pi) => {
      if (item.visible === false) return;
      const panelKey = item.id ?? `p-${pi}`;
      if (item.expanded) panels.add(panelKey);

      const walkItems = (list: PanelMenuItem[], prefix: string) => {
        list.forEach((it, i) => {
          if (it.visible === false) return;
          const key = `${prefix}.${i}`;
          if (it.expanded && it.items?.length) items.add(key);
          if (it.items?.length) walkItems(it.items, key);
        });
      };
      if (item.items?.length) walkItems(item.items, panelKey);
    });

    this._openPanels   = panels;
    this._expandedItems = items;
  }

  /**
   * Walk the items tree to find the key for `target`, then open the
   * parent panel and expand all ancestor items so the selection is visible.
   */
  private _resolveActiveItem(target: PanelMenuItem): void {
    const result = this._findItemKey(target);
    if (!result) return;

    this._selectedKey = result.key;

    const nextPanels = new Set(this._openPanels);
    nextPanels.add(result.panelKey);
    this._openPanels = nextPanels;

    if (result.expandKeys.length) {
      const nextExpanded = new Set(this._expandedItems);
      result.expandKeys.forEach(k => nextExpanded.add(k));
      this._expandedItems = nextExpanded;
    }
  }

  /**
   * Find the dot-path key for a given item object.
   * Matches by `id` (if both have one) or by object reference.
   * Returns the item's key, its root panel key, and all ancestor item keys
   * that must be expanded to make the item visible.
   */
  private _findItemKey(
    target: PanelMenuItem,
  ): { key: string; panelKey: string; expandKeys: string[] } | null {
    const matches = (it: PanelMenuItem) =>
      target.id && it.id ? it.id === target.id : it === target;

    for (let pi = 0; pi < this.items.length; pi++) {
      const panel = this.items[pi];
      if (panel.visible === false) continue;
      const panelKey = panel.id ?? `p-${pi}`;

      // Leaf root panel (no children)
      if (!panel.items?.length && matches(panel)) {
        return { key: panelKey, panelKey, expandKeys: [] };
      }
      if (panel.items?.length) {
        const found = this._walkItemsForKey(panel.items, panelKey, matches, []);
        if (found) return { ...found, panelKey };
      }
    }
    return null;
  }

  private _walkItemsForKey(
    list: PanelMenuItem[],
    prefix: string,
    matches: (it: PanelMenuItem) => boolean,
    ancestorKeys: string[],
  ): { key: string; expandKeys: string[] } | null {
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      if (item.visible === false || item.separator) continue;
      const key = `${prefix}.${i}`;
      if (!item.items?.length && matches(item)) {
        return { key, expandKeys: ancestorKeys };
      }
      if (item.items?.length) {
        const found = this._walkItemsForKey(item.items, key, matches, [...ancestorKeys, key]);
        if (found) return found;
      }
    }
    return null;
  }

  /* ── Panel (root-level) toggling ─────────────────────────────────── */

  private _togglePanel(item: PanelMenuItem, panelKey: string): void {
    if (item.disabled) return;

    const isOpen = this._openPanels.has(panelKey);
    const next   = new Set(this._openPanels);

    if (isOpen) {
      next.delete(panelKey);
      emit<PanelMenuCloseEventDetail>(this, 'uwc-panel-close', { item });
    } else {
      if (!this.multiple) next.clear();
      next.add(panelKey);
      emit<PanelMenuOpenEventDetail>(this, 'uwc-panel-open', { item });
    }
    this._openPanels = next;
  }

  /* ── Nested item toggling ────────────────────────────────────────── */

  private _toggleItem(item: PanelMenuItem, key: string): void {
    const isOpen = this._expandedItems.has(key);
    const next   = new Set(this._expandedItems);
    if (isOpen) {
      // Collapse this item and all its descendants
      for (const k of next) {
        if (k === key || k.startsWith(key + '.')) next.delete(k);
      }
      emit<PanelMenuCollapseEventDetail>(this, 'uwc-item-collapse', { item, key });
    } else {
      next.add(key);
      emit<PanelMenuExpandEventDetail>(this, 'uwc-item-expand', { item, key });
    }
    this._expandedItems = next;
  }

  /* ── Item execution ──────────────────────────────────────────────── */

  private _executeItem(item: PanelMenuItem, e: Event, key: string): void {
    if (item.disabled) return;
    this._selectedKey = key;
    item.command?.({ originalEvent: e, item: item as unknown as import('../types.js').MenuItem });
    emit<MenuItemSelectEventDetail>(this, 'uwc-item-select', {
      item: item as unknown as import('../types.js').MenuItem,
      originalEvent: e,
    });
  }

  /* ── Keyboard navigation ─────────────────────────────────────────── */

  /** Collect all focusable item keys in document order (panels + nested). */
  private _allFocusableKeys(): string[] {
    const keys: string[] = [];

    this.items.forEach((panel, pi) => {
      if (panel.visible === false || panel.disabled) return;
      const panelKey = panel.id ?? `p-${pi}`;
      keys.push(panelKey); // panel header

      if (this._openPanels.has(panelKey) && panel.items?.length) {
        this._collectItemKeys(panel.items, panelKey, keys);
      }
    });

    return keys;
  }

  private _collectItemKeys(list: PanelMenuItem[], prefix: string, out: string[]): void {
    list.forEach((it, i) => {
      if (it.visible === false || it.separator) return;
      const key = `${prefix}.${i}`;
      if (!it.disabled) out.push(key);
      if (it.items?.length && this._expandedItems.has(key)) {
        this._collectItemKeys(it.items, key, out);
      }
    });
  }

  private _handleKeydown(e: KeyboardEvent, key: string, item: PanelMenuItem, isPanelHeader: boolean): void {
    const allKeys = this._allFocusableKeys();
    const pos     = allKeys.indexOf(key);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = allKeys[pos + 1];
        if (next) this._focusKey(next);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = allKeys[pos - 1];
        if (prev) this._focusKey(prev);
        break;
      }
      case 'Home': {
        e.preventDefault();
        if (allKeys[0]) this._focusKey(allKeys[0]);
        break;
      }
      case 'End': {
        e.preventDefault();
        const last = allKeys.at(-1);
        if (last) this._focusKey(last);
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        if (isPanelHeader) {
          if (!this._openPanels.has(key)) this._togglePanel(item, key);
        } else if (item.items?.length) {
          if (!this._expandedItems.has(key)) this._toggleItem(item, key);
          // Focus first child after expand
          requestAnimationFrame(() => {
            const firstChild = allKeys[pos + 1];
            if (firstChild) this._focusKey(firstChild);
          });
        }
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        if (isPanelHeader) {
          if (this._openPanels.has(key)) this._togglePanel(item, key);
        } else if (item.items?.length && this._expandedItems.has(key)) {
          this._toggleItem(item, key);
        } else {
          // Move to parent
          const parentKey = key.includes('.') ? key.slice(0, key.lastIndexOf('.')) : null;
          if (parentKey) this._focusKey(parentKey);
        }
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        if (isPanelHeader) {
          if (item.items?.length) {
            this._togglePanel(item, key);
          } else {
            this._executeItem(item, e, key);
          }
        } else if (item.items?.length) {
          this._toggleItem(item, key);
        } else {
          this._executeItem(item, e, key);
        }
        break;
      }
    }
  }

  private _focusKey(key: string): void {
    this._focusedKey = key;
    requestAnimationFrame(() => {
      this.shadowRoot?.querySelector<HTMLElement>(`[data-pm-key="${CSS.escape(key)}"]`)?.focus();
    });
  }

  /* ── Render helpers ──────────────────────────────────────────────── */

  /** Render a root-level panel (accordion header + collapsible content). */
  private _renderPanel(item: PanelMenuItem, panelIdx: number): TemplateResult {
    if (item.visible === false) return html``;

    const panelKey = item.id ?? `p-${panelIdx}`;
    const isOpen   = this._openPanels.has(panelKey);
    const hasItems = Boolean(item.items?.length);

    const headerContent = html`
      ${item.icon
        ? html`<span class="uwc-pm__h-icon ${item.iconClass ?? ''}" aria-hidden="true">
            <uwc-icon name=${item.icon} size="1.2em" library=${item.iconLibrary ?? 'default'}></uwc-icon>
          </span>`
        : nothing}
      <span class="uwc-pm__h-label">${item.label ?? ''}</span>
      ${item.badge
        ? html`<span class="uwc-pm__badge ${item.badgeClass ?? ''}">${item.badge}</span>`
        : nothing}
      ${hasItems
        ? html`<span class="uwc-pm__h-chevron ${isOpen ? 'uwc-pm__h-chevron--open' : ''}" aria-hidden="true">
            <uwc-icon name="chevron-down" size="0.9em"></uwc-icon>
          </span>`
        : nothing}`;

    // Panel header — link if url (leaf), button if has children or command
    const header = item.url && !hasItems
      ? html`
        <a
          id="uwc-pm-h-${panelKey}"
          href=${item.url}
          target=${item.target ?? '_self'}
          class=${classMap({
            'uwc-pm__header':          true,
            'uwc-pm__header--active':  isOpen,
            'uwc-pm__header--disabled':!!item.disabled,
            'uwc-pm__header--selected':this._selectedKey === panelKey,
          })}
          data-pm-key=${panelKey}
          title=${item.title ?? ''}
          role="treeitem"
          aria-expanded=${hasItems ? String(isOpen) : 'false'}
          aria-current=${this._selectedKey === panelKey ? 'true' : 'false'}
          tabindex=${this._focusedKey === panelKey || (!this._focusedKey && panelIdx === 0) ? '0' : '-1'}
          @click=${(e: MouseEvent) => { e.stopPropagation(); this._executeItem(item, e, panelKey); }}
          @keydown=${(e: KeyboardEvent) => this._handleKeydown(e, panelKey, item, true)}
          @focus=${() => { this._focusedKey = panelKey; }}
        >${headerContent}</a>`
      : html`
        <button
          id="uwc-pm-h-${panelKey}"
          type="button"
          class=${classMap({
            'uwc-pm__header':          true,
            'uwc-pm__header--active':  isOpen,
            'uwc-pm__header--disabled':!!item.disabled,
            'uwc-pm__header--selected':!hasItems && this._selectedKey === panelKey,
          })}
          data-pm-key=${panelKey}
          ?disabled=${item.disabled}
          aria-expanded=${hasItems ? String(isOpen) : 'false'}
          aria-controls=${hasItems ? `uwc-pm-body-${panelKey}` : ''}
          aria-current=${!hasItems && this._selectedKey === panelKey ? 'true' : 'false'}
          title=${item.title ?? ''}
          role="treeitem"
          tabindex=${this._focusedKey === panelKey || (!this._focusedKey && panelIdx === 0) ? '0' : '-1'}
          @click=${(e: MouseEvent) => {
            e.stopPropagation();
            if (item.disabled) return;
            hasItems ? this._togglePanel(item, panelKey) : this._executeItem(item, e, panelKey);
          }}
          @keydown=${(e: KeyboardEvent) => this._handleKeydown(e, panelKey, item, true)}
          @focus=${() => { this._focusedKey = panelKey; }}
        >${headerContent}</button>`;

    return html`
      <div
        class=${classMap({
          'uwc-pm__panel':       true,
          'uwc-pm__panel--open': isOpen,
          'uwc-pm__panel--last': panelIdx === this.items.filter(it => it.visible !== false).length - 1,
        })}
        role="none"
      >
        ${header}

        ${hasItems ? html`
          <div
            id="uwc-pm-body-${panelKey}"
            class=${classMap({ 'uwc-pm__body': true, 'uwc-pm__body--open': isOpen })}
            role="group"
            aria-labelledby="uwc-pm-h-${panelKey}"
          >
            <div class="uwc-pm__body-inner">
              <ul
                class="uwc-pm__items"
                role="tree"
                aria-label=${item.label ?? ''}
              >
                ${this._renderItems(item.items ?? [], panelKey, 0)}
              </ul>
            </div>
          </div>` : nothing}
      </div>`;
  }

  /** Recursively render a list of nested items. */
  private _renderItems(
    list:   PanelMenuItem[],
    prefix: string,
    depth:  number,
  ): TemplateResult {
    return html`${list
      .filter(it => it.visible !== false)
      .map((item, i) => this._renderItem(item, `${prefix}.${i}`, prefix, depth))}`;
  }

  /** Render a single nested item (separator, leaf, or parent). */
  private _renderItem(
    item:      PanelMenuItem,
    key:       string,
    parentKey: string,
    depth:     number,
  ): TemplateResult {
    if (item.separator) {
      return html`
        <li role="separator" aria-hidden="true" class="uwc-pm__separator"></li>`;
    }

    const hasChildren = Boolean(item.items?.length);
    const isExpanded  = this._expandedItems.has(key);
    const isFocused   = this._focusedKey === key;
    const isSelected  = !hasChildren && this._selectedKey === key;

    const inner = html`
      ${hasChildren
        ? html`<span class=${classMap({
            'uwc-pm__toggler': true,
            'uwc-pm__toggler--open': isExpanded,
          })} aria-hidden="true">
            <uwc-icon name="chevron-right" size="0.85em"></uwc-icon>
          </span>`
        : html`<span class="uwc-pm__toggler-placeholder" aria-hidden="true"></span>`}
      ${item.icon
        ? html`<span class="uwc-pm__item-icon ${item.iconClass ?? ''}" aria-hidden="true">
            <uwc-icon name=${item.icon} size="1.05em" library=${item.iconLibrary ?? 'default'}></uwc-icon>
          </span>`
        : nothing}
      <span class="uwc-pm__item-label">${item.label ?? ''}</span>
      ${item.badge
        ? html`<span class="uwc-pm__badge ${item.badgeClass ?? ''}">${item.badge}</span>`
        : nothing}`;

    const itemCls = classMap({
      'uwc-pm__item':           true,
      'uwc-pm__item--danger':   !!item.danger,
      'uwc-pm__item--disabled': !!item.disabled,
      'uwc-pm__item--focused':  isFocused,
      'uwc-pm__item--expanded': isExpanded,
      'uwc-pm__item--selected': isSelected,
    });

    const handlers = {
      click: (e: MouseEvent) => {
        e.stopPropagation();
        if (item.disabled) return;
        hasChildren ? this._toggleItem(item, key) : this._executeItem(item, e, key);
      },
      keydown: (e: KeyboardEvent) => this._handleKeydown(e, key, item, false),
      focus: () => { this._focusedKey = key; },
    };

    const itemEl = item.url && !hasChildren && !item.disabled
      ? html`
        <a
          href=${item.url}
          target=${item.target ?? '_self'}
          role="treeitem"
          class=${itemCls}
          data-pm-key=${key}
          title=${item.title ?? ''}
          tabindex="-1"
          aria-level=${depth + 2}
          aria-current=${isSelected ? 'true' : 'false'}
          style="--_depth:${depth}"
          @click=${handlers.click}
          @keydown=${handlers.keydown}
          @focus=${handlers.focus}
        >${inner}</a>`
      : html`
        <button
          type="button"
          role="treeitem"
          class=${itemCls}
          data-pm-key=${key}
          ?disabled=${item.disabled}
          title=${item.title ?? ''}
          tabindex="-1"
          aria-expanded=${hasChildren ? String(isExpanded) : 'false'}
          aria-level=${depth + 2}
          aria-current=${isSelected ? 'true' : 'false'}
          style="--_depth:${depth}"
          @click=${handlers.click}
          @keydown=${handlers.keydown}
          @focus=${handlers.focus}
        >${inner}</button>`;

    return html`
      <li role="none" class="uwc-pm__item-li">
        ${itemEl}
        ${hasChildren ? html`
          <div class=${classMap({ 'uwc-pm__sub': true, 'uwc-pm__sub--open': isExpanded })}>
            <div class="uwc-pm__sub-inner">
              <ul
                role="group"
                class="uwc-pm__items"
                aria-label=${item.label ?? ''}
              >
                ${this._renderItems(item.items ?? [], key, depth + 1)}
              </ul>
            </div>
          </div>` : nothing}
      </li>`;
  }

  /* ── Root render ─────────────────────────────────────────────────── */

  protected override render(): TemplateResult {
    const visibleItems = this.items.filter(it => it.visible !== false);

    return html`
      <nav
        class=${classMap({
          'uwc-pm': true,
          'uwc-pm--scrollable': Boolean(this.scrollableHeight),
        })}
        style=${this.scrollableHeight ? `max-height: ${this.scrollableHeight}; overflow-y: auto;` : ''}
        aria-label=${this.ariaLabel}
        role="tree"
        aria-multiselectable=${String(this.multiple)}
      >
        ${repeat(
          visibleItems,
          (item, i) => item.id ?? `p-${i}`,
          (item, i) => this._renderPanel(item, i),
        )}
      </nav>`;
  }
}
